import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import { TIRE_SET_DOCUMENTS_STEP, TIRE_SET_INTRO_TEXTS, TIRE_SET_TIRES_STEP, TIRE_SET_WAREHOUSE_STEP } from "./consts";
import TireSetDocumentsScene from "./scenes/TireSetDocumentsScene";
import TireSetTiresScene from "./scenes/TireSetTiresScene";
import TireSetWarehouseScene from "./scenes/TireSetWarehouseScene";
import { TireSetIntroProps, TireSetSceneProps } from "./types";

/** Scena za korak čarobnjaka seta guma. */
const STEP_SCENES: Record<number, React.FC<TireSetSceneProps>> = {
  [TIRE_SET_TIRES_STEP]: TireSetTiresScene,
  [TIRE_SET_WAREHOUSE_STEP]: TireSetWarehouseScene,
  [TIRE_SET_DOCUMENTS_STEP]: TireSetDocumentsScene,
};

/**
 * Lijevi panel seta guma: za korak vlastita scena s karticama koje prikazuju upisane podatke,
 * a ispod objašnjenje koraka. Isti obrazac kao intro čarobnjaka vozila.
 */
const TireSetIntro: React.FC<TireSetIntroProps> = ({ formValues, stepCurrent, stepCount, isNewTireSet }) => {
  const stepKey = STEP_SCENES[stepCurrent] ? stepCurrent : TIRE_SET_TIRES_STEP;
  const Scene = STEP_SCENES[stepKey];
  const text = TIRE_SET_INTRO_TEXTS[stepKey];
  // Postojeći set se uređuje, pa prvi korak nema smisla zvati slaganjem novog seta.
  const title = !isNewTireSet && stepKey === TIRE_SET_TIRES_STEP ? "Uredite set guma" : text.title;

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: novi korak iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={stepKey}>
          <Scene formValues={formValues} />
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
          Korak {stepKey + 1} od {stepCount}
        </div>
        <div key={stepKey} className="motion-safe:animate-showcaseFadeIn">
          <div className="mt-2 text-3xl font-bold leading-tight text-white">{title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{text.description}</p>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: stepCount }, (_, index) => (
            <span
              key={index}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                index === stepKey ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(TireSetIntro);
